import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api from '@/lib/api'
import type {
  CatalogItem,
  CatalogItemCreate,
  CatalogItemUpdate,
  CatalogItemType,
  BillingType,
  PaginatedResponse,
} from '@/types'

interface CatalogListParams {
  page?: number
  per_page?: number
  search?: string
  item_type?: CatalogItemType | ''
  billing_type?: BillingType | ''
  category?: string
  is_active?: boolean | null
}

export function useCatalogItems(params?: CatalogListParams) {
  return useQuery({
    queryKey: ['catalog', params],
    queryFn: async () => {
      const query: Record<string, string | number | boolean> = {}
      if (params?.page) query.page = params.page
      if (params?.per_page) query.per_page = params.per_page
      if (params?.search) query.search = params.search
      if (params?.item_type) query.item_type = params.item_type
      if (params?.billing_type) query.billing_type = params.billing_type
      if (params?.category) query.category = params.category
      if (params?.is_active !== undefined && params?.is_active !== null) query.is_active = params.is_active
      const { data } = await api.get<PaginatedResponse<CatalogItem>>('/v1/catalog/', { params: query })
      return data
    },
  })
}

export function useCatalogCategories() {
  return useQuery({
    queryKey: ['catalog', 'categories'],
    queryFn: async () => {
      const { data } = await api.get<string[]>('/v1/catalog/categories')
      return data
    },
  })
}

export function useCatalogItem(id: string | null) {
  return useQuery({
    queryKey: ['catalog', id],
    queryFn: async () => {
      const { data } = await api.get<CatalogItem>(`/v1/catalog/${id}`)
      return data
    },
    enabled: !!id,
  })
}

export function useCreateCatalogItem() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (item: CatalogItemCreate) => {
      const { data } = await api.post<CatalogItem>('/v1/catalog/', item)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['catalog'] })
    },
  })
}

export function useUpdateCatalogItem() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, data: item }: { id: string; data: CatalogItemUpdate }) => {
      const { data } = await api.put<CatalogItem>(`/v1/catalog/${id}`, item)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['catalog'] })
    },
  })
}

export function useDeleteCatalogItem() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/v1/catalog/${id}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['catalog'] })
    },
  })
}
